import React, { useEffect, useState } from 'react'
import Layout from '../component/layout'
import { Link, useNavigate } from 'react-router-dom'
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { getLandlordIncomeURL } from "../../../utils/urls";
import { filterObjectsByAllProperties } from '../../../utils/filterSearch';


const RevenueReports = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState('All');
  const [filteredInvoices, setFilteredInvoices] = useState([]);


  const [summary, setSummary] = useState({
    totalIncome: 0,
    totalPaid: 0,
    totalBalance: 0
  });

  const handleStatusFilter = (e) => {
    const selected = e.target.value;
    setStatusFilter(selected);

    if (selected === 'All') {
      setFilteredInvoices(invoices);
    } else {
      const filtered = invoices.filter(inv => inv.status === selected);
      setFilteredInvoices(filtered);
    }
  };

  const [dateFilter, setDateFilter] = useState({
    month: '',
    year: '',
    startDate: '',
    endDate: ''
  });
  const [filteredSummary, setFilteredSummary] = useState({
    totalIncome: 0,
    totalPaid: 0,
    totalBalance: 0
  });

  // Helper function to get current year and generate year options
  const getCurrentYear = () => new Date().getFullYear();
  const getYearOptions = () => {
    const currentYear = getCurrentYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 2; i++) {
      years.push(i);
    }
    return years;
  };

  // Helper function to filter invoices by date range
  const filterInvoicesByDateRange = (invoices, filters) => {
    if (!filters.startDate && !filters.endDate && !filters.month && !filters.year) {
      return invoices;
    }

    return invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.issueDate);
      let matchesFilter = true;

      // Filter by month and year
      if (filters.month && filters.year) {
        const invoiceMonth = invoiceDate.getMonth() + 1; // getMonth() returns 0-11
        const invoiceYear = invoiceDate.getFullYear();
        matchesFilter = matchesFilter &&
          invoiceMonth === parseInt(filters.month) &&
          invoiceYear === parseInt(filters.year);
      } else if (filters.year) {
        const invoiceYear = invoiceDate.getFullYear();
        matchesFilter = matchesFilter && invoiceYear === parseInt(filters.year);
      }

      // Filter by date range
      if (filters.startDate) {
        const startDate = new Date(filters.startDate);
        matchesFilter = matchesFilter && invoiceDate >= startDate;
      }

      if (filters.endDate) {
        const endDate = new Date(filters.endDate);
        matchesFilter = matchesFilter && invoiceDate <= endDate;
      }

      return matchesFilter;
    });
  };

  // Function to calculate summary from filtered invoices (UPDATED to exclude voided invoices)
  const calculateSummaryFromInvoices = (invoices) => {
    // Filter out voided invoices for calculations
    const activeInvoices = invoices.filter(invoice =>
      invoice.status && invoice.status.toLowerCase() !== 'void' && invoice.status.toLowerCase() !== 'voided'
    );

    const totalIncome = activeInvoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);

    const totalPaid = activeInvoices.reduce((sum, invoice) => {
      if (invoice.status === 'Paid') {
        return sum + (invoice.totalAmount || 0);
      } else if (invoice.status === 'Partially Paid') {
        return sum + (invoice.paidAmount || 0);
      }
      return sum;
    }, 0);

    const totalBalance = activeInvoices.reduce((sum, invoice) => {
      if (invoice.status === 'Overdue') {
        return sum + (invoice.totalAmount || 0);
      }
      return sum;
    }, 0);

    return {
      totalIncome,
      totalPaid,
      totalBalance
    };
  };

  // Handle date filter changes
  const handleDateFilterChange = (field, value) => {
    const newFilter = { ...dateFilter, [field]: value };
    setDateFilter(newFilter);

    // Apply filters to invoices
    const filtered = filterInvoicesByDateRange(invoices, newFilter);
    setFilteredInvoices(filtered);

    // Calculate new summary
    const newSummary = calculateSummaryFromInvoices(filtered);
    setFilteredSummary(newSummary);
  };

  // Reset filters
  const resetFilters = () => {
    setDateFilter({
      month: '',
      year: '',
      startDate: '',
      endDate: ''
    });
    setFilteredInvoices(invoices);
    setFilteredSummary(summary);
  };

  const fetchLandlordIncome = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const homeOwnerId = await getItem('selectedCustomerId');
      const response = await makeRequest2(`${getLandlordIncomeURL}/${facilityId}/${homeOwnerId}`, 'GET', {});
      if (response.success && response.data) {
        setInvoices(response.data.invoices);
        setFilteredInvoices(response.data.invoices);
        const summaryData = {
          totalIncome: response.data.totalIncome || 0,
          totalPaid: response.data.totalPaid || 0,
          totalBalance: response.data.totalBalance || 0
        };
        setSummary(summaryData);
        setFilteredSummary(summaryData); // Initialize filtered summary
      } else {
        toastify('No invoices found for tenants', 'warn');
      }
    } catch (err) {
      toastify(err.message, 'error');
    }
  };

  const actionTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="ti ti-eye"
          className="p-button-rounded p-button-success p-ml-2"
          onClick={(e) => {
            e.stopPropagation();
            handleViewInvoice(rowData._id);
          }}
          tooltip="View Details"
        />
      </div>
    );
  };

  const statusTemplate = (rowData) => {
    const statusStyles = {
      Paid: { color: '#28a745', backgroundColor: '#e9fbe9' },
      Pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
      Overdue: { color: '#dc3545', backgroundColor: '#fde9e9' }
    }

    const style = statusStyles[rowData.status] || {
      color: '#6c757d',
      backgroundColor: '#f1f1f1'
    }

    return (
      <span
        style={{
          ...style,
          padding: '0.5em 1em',
          borderRadius: '12px',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          display: 'inline-block'
        }}
      >
        {rowData.status}
      </span>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currencyCode = 'KES') => {
    if (typeof amount !== 'number') return `${currencyCode} 0.00`;
    return `${currencyCode} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  // Get currency code for individual invoice
  const getInvoiceCurrency = (invoice) => {
    // Try to get currency from the invoice object if it exists
    if (invoice.currency && invoice.currency.code) {
      return invoice.currency.code;
    }

    // Default to KES if no currency indicator is found
    return 'KES';
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/portal/view_invoice/${invoiceId}`);
  };

  const handleInputSearch = async (value) => {
    try {
      setSearchTerm(value);
      if (value === '') {
        fetchLandlordIncome()
      } else {
        const results = await filterObjectsByAllProperties(filteredInvoices, value);
        setFilteredInvoices(results);
      }
    } catch (error) {
      toastify(error.message, 'error');
    }
  };

  useEffect(() => {
    fetchLandlordIncome();
  }, []);


  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={"/landlord_portal"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"#"}>Key Metrics</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"/portal/metrics/revenue_reports"}>Revenue Reports</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="row">
            <div className="col-sm-12">

              <div className="row">
                <div className="col-sm-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="container mt-4">
                        <h4 className='mb-4'>Lease Income</h4>

                        <div className="card mb-4">
                          <div className="card-body">
                            <h6 className="card-title mb-3">Filter by Date Range</h6>
                            <div className="row g-3">
                              <div className="col-md-2">
                                <label className="form-label fw-bold">Month</label>
                                <select
                                  className="form-select"
                                  value={dateFilter.month}
                                  onChange={(e) => handleDateFilterChange('month', e.target.value)}
                                >
                                  <option value="">All Months</option>
                                  <option value="1">January</option>
                                  <option value="2">February</option>
                                  <option value="3">March</option>
                                  <option value="4">April</option>
                                  <option value="5">May</option>
                                  <option value="6">June</option>
                                  <option value="7">July</option>
                                  <option value="8">August</option>
                                  <option value="9">September</option>
                                  <option value="10">October</option>
                                  <option value="11">November</option>
                                  <option value="12">December</option>
                                </select>
                              </div>

                              <div className="col-md-2">
                                <label className="form-label fw-bold">Year</label>
                                <select
                                  className="form-select"
                                  value={dateFilter.year}
                                  onChange={(e) => handleDateFilterChange('year', e.target.value)}
                                >
                                  <option value="">All Years</option>
                                  {getYearOptions().map(year => (
                                    <option key={year} value={year}>{year}</option>
                                  ))}
                                </select>
                              </div>

                              <div className="col-md-3">
                                <label className="form-label fw-bold">Start Date</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={dateFilter.startDate}
                                  onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
                                />
                              </div>

                              <div className="col-md-3">
                                <label className="form-label fw-bold">End Date</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={dateFilter.endDate}
                                  onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
                                />
                              </div>

                              <div className="col-md-2 d-flex align-items-end">
                                <button
                                  className="btn btn-outline-secondary w-100"
                                  onClick={resetFilters}
                                >
                                  Reset Filters
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-3">
                            <div className="p-3 mb-3" style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '8px' }}>
                              <h5 className="text-primary">Total Expected</h5>
                              <h4 className="fw-bold text-primary">
                                KES {(Object.keys(dateFilter).some(key => dateFilter[key]) ? filteredSummary.totalIncome : summary.totalIncome || 0).toLocaleString()}
                              </h4>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="p-3 mb-3" style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '8px' }}>
                              <h5 className="text-success">Paid</h5>
                              <h4 className="fw-bold text-success">
                                KES {(Object.keys(dateFilter).some(key => dateFilter[key]) ? filteredSummary.totalPaid : summary.totalPaid || 0).toLocaleString()}
                              </h4>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="p-3 mb-3" style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '8px' }}>
                              <h5 className="text-warning">Unpaid</h5>
                              <h4 className="fw-bold text-warning">
                                KES {(Object.keys(dateFilter).some(key => dateFilter[key]) ?
                                  (filteredSummary.totalIncome - filteredSummary.totalPaid) :
                                  ((summary.totalIncome || 0) - (summary.totalPaid || 0))
                                ).toLocaleString()}
                              </h4>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="p-3 mb-3" style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '8px' }}>
                              <h5 className="text-danger">Overdue</h5>
                              <h4 className="fw-bold text-danger">
                                KES {(Object.keys(dateFilter).some(key => dateFilter[key]) ? filteredSummary.totalBalance : summary.totalBalance || 0).toLocaleString()}
                              </h4>
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-12">
                            <div className="card">
                              <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                  <input
                                    type="text"
                                    className="form-control"
                                    style={{ maxWidth: "250px" }}
                                    placeholder="Search Invoice #"
                                    value={searchTerm}
                                    onChange={(e) => handleInputSearch(e.target.value)}
                                  />

                                  <div className="d-flex align-items-center ms-auto">
                                    <label className="fw-bold me-2">Filter by Status:</label>
                                    <select
                                      className="form-control"
                                      style={{ width: "250px" }}
                                      value={statusFilter}
                                      onChange={handleStatusFilter}
                                    >
                                      {["All", "Unpaid", "Pending", "Paid", "Overdue", "Cancelled", "Partially Paid"].map(status => (
                                        <option key={status} value={status}>{status}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                <DataTable
                                  value={filteredInvoices}
                                  emptyMessage="No invoices found."
                                  sortMode="multiple"
                                  paginator
                                  rows={5}
                                  stripedRows
                                  tableStyle={{ minWidth: '50rem' }}
                                >
                                  <Column field="invoiceNumber" header="Invoice Number" sortable />

                                  <Column
                                    field="totalAmount"
                                    header="Amount"
                                    style={{ minWidth: "120px" }}
                                    body={(rowData) => {
                                      const currencyCode = getInvoiceCurrency(rowData);
                                      return formatCurrency(rowData.totalAmount, currencyCode);
                                    }}
                                    sortable
                                  />
                                  <Column
                                    field="issueDate"
                                    header="Issue Date"
                                    sortable
                                    body={(rowData) => formatDate(rowData.issueDate)}
                                  />
                                  <Column
                                    field="dueDate"
                                    header="Due Date"
                                    sortable
                                    body={(rowData) => formatDate(rowData.dueDate)}
                                  />
                                  <Column
                                    field="status"
                                    header="Status"
                                    sortable
                                    body={statusTemplate}
                                  />
                                  <Column
                                    field="actions"
                                    header="Actions"
                                    body={actionTemplate}
                                  />
                                </DataTable>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default RevenueReports